import { createFileRoute, notFound } from "@tanstack/react-router"
import * as z from "zod"

import { generateMetaTags } from "@repo/seo/generate-meta-tags"
import { If } from "@repo/ui/components/ui/if"
import { pick } from "@repo/utils/collections"
import { sharedConfig } from "~/lib/config/shared"
import { AuthLayout } from "~/modules/auth/components/auth-layout"
import { AcceptInvitationForm } from "~/modules/invitations/components/accept-invitation-form"
import { ResendInvitation } from "~/modules/invitations/components/resend-invitation"
import { getInvitationByTokenFn } from "~/modules/invitations/invitations.functions"

const searchSchema = z.object({
  token: z.string(),
  otp: z.coerce
    .string()
    .refine((code) => code !== "undefined")
    .catch(""),
})

export const Route = createFileRoute("/_auth-layout/invitation")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => pick(search, ["token"]),
  beforeLoad: async ({ search }) => {
    if (!search.token) {
      throw notFound()
    }
  },
  loader: async ({ deps: { token } }) => {
    const result = await getInvitationByTokenFn({ data: { token } })

    if (!result.success || !result.data) {
      throw notFound()
    }

    return {
      invitation: result.data,
    }
  },
  component: RouteComponent,
  preload: false,
  head: ({ match, loaderData }) => ({
    meta: generateMetaTags({
      base: {
        title: `Accept Invitation | ${sharedConfig.appName}`,
        description: `Accept invitation to ${loaderData?.invitation?.unit_name ?? sharedConfig.appName}`,
        url: new URL(match.fullPath, sharedConfig.baseUrl).toString(),
      },
      robots: ["noindex", "nofollow", "noarchive"],
    }),
  }),
})

function RouteComponent() {
  const { invitation } = Route.useLoaderData()
  const { token, otp } = Route.useSearch()

  return (
    <AuthLayout
      title="Accept Invitation"
      footer={
        <If condition={token}>{() => <ResendInvitation token={token} />}</If>
      }
    >
      <AcceptInvitationForm invitation={invitation} token={token} otp={otp} />
    </AuthLayout>
  )
}
